import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { splitDate } from '../../../helper/dateFormat';
import { selectLastDays } from '../../../helper/selectLastDays';
import { PRIMARY_ORANGE } from '../../../constants/theme';
import { getCompanyStructure } from '../../../redux/actions/structure';
import { getTopicFilteredEvolution } from '../../../redux/actions/topics';
import Grade from '../../global/Grade/Grade';
import './InvestmentResume.scss';

const PERIOD_OPTIONS = ['últimos 30 dias', 'últimos 60 dias', 'últimos 90 dias'];

const InvestmentResume = ({ investment, topicId, investmentDetails, setShowModal, edit, investmentTasks, topic }) => {
  const dispatch = useDispatch();
  const structure = useSelector(state => state?.structure?.data);
  const [gradeVariation, setGradeVariation] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [roles, setRoles] = useState([]);
  const [period, setPeriod] = useState(PERIOD_OPTIONS[0]);

  // Resolve topicId: aceita prop direta ou calcula do investment
  const resolvedTopicId = topicId ?? (topic
    ? (investment?.topic?.id ?? investment?.id)
    : investment?.id);

  const topicName = topic
    ? (investment?.topic?.name ?? investment?.name)
    : investment?.name;

  const getFromDate = useCallback(() => {
    const days = selectLastDays(period);
    const d = new Date();
    d.setDate(d.getDate() - days);
    return splitDate(d.toISOString());
  }, [period]);

  useEffect(() => {
    dispatch(getCompanyStructure());
  }, [dispatch]);

  useEffect(() => {
    if (!resolvedTopicId) return;
    const payload = { from: getFromDate(), role: selectedRole };
    if (selectedUnit) payload.unit = selectedUnit;

    setLoading(true);
    dispatch(getTopicFilteredEvolution(resolvedTopicId, payload))
      .then(data => setGradeVariation(data))
      .catch(() => setGradeVariation([]))
      .finally(() => setLoading(false));
  }, [resolvedTopicId, period, selectedRole, selectedUnit, dispatch]);

  const handleUnitChange = (e) => {
    const unit = e.target.value;
    setSelectedUnit(unit);
    setSelectedRole('');
    const found = Array.isArray(structure) ? structure.find(u => u.name === unit) : null;
    setRoles(found?.roles || []);
  };

  const chartData = useMemo(() => {
    const raw = Array.isArray(gradeVariation) && gradeVariation.length > 0
      ? gradeVariation
      : (investmentDetails?.progression || []);

    const months = ['JAN','FEV','MAR','ABR','MAI','JUN','JUL','AGO','SET','OUT','NOV','DEZ'];
    const grouped = raw.reduce((acc, item) => {
      const d = new Date(item.date);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (!acc[key]) acc[key] = { ...item };
      else {
        acc[key].average = Math.max(acc[key].average, item.average);
        acc[key].max = Math.max(acc[key].max, item.max);
        acc[key].min = Math.min(acc[key].min, item.min);
      }
      return acc;
    }, {});

    return Object.values(grouped).map(item => ({
      ...item,
      monthName: months[new Date(item.date).getMonth()]
    }));
  }, [gradeVariation, investmentDetails?.progression]);

  const tracking = investment?.tracking;
  const isUp = (tracking?.variation ?? 0) > 0;

  return (
    <div className="ir-root">

      <div className="ir-filters">
        <select className="ir-select" value={selectedUnit} onChange={handleUnitChange}>
          <option value="">Selecione uma área</option>
          {Array.isArray(structure) && structure.map(u => (
            <option key={u.name} value={u.name}>{u.name}</option>
          ))}
        </select>

        <select className="ir-select" value={selectedRole} onChange={e => setSelectedRole(e.target.value)} disabled={!selectedUnit}>
          <option value="">Selecionar nível</option>
          {roles.map(r => <option key={r} value={r}>{r}</option>)}
        </select>

        <select className="ir-select" value={period} onChange={e => setPeriod(e.target.value)}>
          {PERIOD_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      <div className="ir-content">
        <div className="ir-chart-col">
          <p className="ir-chart-title">
            Evolução de <span>{topicName}</span>
          </p>
          <div className="ir-chart">
            {loading ? (
              <div className="ir-loading">Carregando...</div>
            ) : chartData.length === 0 ? (
              <div className="ir-loading">Sem dados para o período selecionado.</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="avgGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={PRIMARY_ORANGE} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={PRIMARY_ORANGE} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis
                    dataKey="monthName"
                    tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    domain={[0, 10]}
                    tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'rgba(8,16,52,0.95)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: 8,
                      color: 'white',
                      fontSize: 12
                    }}
                    labelStyle={{ color: 'rgba(255,255,255,0.6)' }}
                    formatter={(v) => [v?.toFixed(1), 'Média']}
                  />
                  <Area
                    type="monotone"
                    dataKey="average"
                    stroke={PRIMARY_ORANGE}
                    strokeWidth={2}
                    fill="url(#avgGradient)"
                    dot={{ fill: PRIMARY_ORANGE, r: 3, strokeWidth: 0 }}
                    activeDot={{ r: 5, fill: PRIMARY_ORANGE, stroke: 'white', strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="ir-info-col">
          <div className="ir-grade">
            <p className="ir-grade-label">Média da avaliação</p>
            <Grade
              expected_score={investment?.expected_score}
              value={tracking?.average ?? 0}
              variation={tracking?.variation}
            />
          </div>

          <p className="ir-trend">
            Na comunidade, este investimento está em <strong>{isUp ? 'alta' : 'baixa'}</strong>.
          </p>

          <p className="ir-trend-desc">
            O valor médio {isUp ? 'subiu' : 'desceu'} — mais pessoas indicaram{' '}
            {isUp ? 'médio a alto' : 'baixo a médio'} grau de proficiência nessa competência.
          </p>

          {investmentTasks && (
            <div className="ir-stats">
              <span>{investmentTasks?.activeTasksCount || 0} aportes</span>
              <span>{investmentTasks?.affiliations || 0} investidores</span>
            </div>
          )}

          {edit && (
            <button className="ir-edit" onClick={() => setShowModal(true)}>
              Editar competência
            </button>
          )}
        </div>
      </div>

    </div>
  );
};

export default InvestmentResume;
